import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { ReactAmChart } from '../ReactAmChart';
import { isEqual } from 'lodash';
import { arrayOf, shape, string, number } from 'prop-types';
import styles from './styles.scss';

export default class StationScatterChart extends ReactAmChart {
  static propTypes = {
    data: arrayOf(
      shape({
        y: number.isRequired,
        x: string.isRequired
      })
    ).isRequired,
    defaultFillColor: string,
    fontColor: string,
    fontFamily: string,
    yAxisTitle: string,
    xAxisFontSize: number,
    titleFontSize: number
  };

  static defaultProps = {
    data: [],
    fontColor: 'rgba(0, 0, 0, 0.54)',
    fontFamily: 'Roboto, "Helvetica Neue", sans-serif',
    defaultFillColor: '#67B7DC',
    xAxisFontSize: 12,
    titleFontSize: 14
  };

  prevChartData;
  columnSeries;
  bulletSeries;
  valueAxis;
  drawFunction(chartId, prevChart, am4core, am4charts) {
    const {
      data,
      fontColor,
      defaultFillColor,
      yAxisTitle,
      xAxisFontSize,
      titleFontSize,
      fontFamily,
      isAvgCost,
      isAdVisit,
    } = this.props;
    let chart = prevChart;

    if (!chart) {
      chart = am4core.create(chartId, am4charts.XYChart);
      const am4fontColor = am4core.color(fontColor);

      // set category X
      const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = 'x';
      categoryAxis.renderer.inversed = false;
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.labels.template.horizontalCenter = 'right';
      categoryAxis.renderer.labels.template.verticalCenter = 'middle';
      categoryAxis.renderer.labels.template.rotation = 270;
      categoryAxis.renderer.minGridDistance = 5;
      categoryAxis.tooltip.disabled = true;
      this.setFont(categoryAxis, xAxisFontSize, am4fontColor, !yAxisTitle && 500, fontFamily);

      // only show top & bottom grid
      categoryAxis.renderer.grid.template.adapter.add('visible', (value, target) => {
        return target.dataItem.index === -1 || target.dataItem.index === 0;
      });

      // set y axis
      this.valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      this.valueAxis.min = 0;
      this.valueAxis.title.fontSize = titleFontSize;
      this.valueAxis.title.fontFamily = fontFamily;
      this.valueAxis.title.fontWeight = 500;
      this.valueAxis.title.fill = am4fontColor;
      this.valueAxis.renderer.minGridDistance = 100;
      this.valueAxis.cursorTooltipEnabled = false;
      this.setFont(this.valueAxis, 12, am4fontColor, 400, fontFamily);

      //set column
      this.columnSeries = chart.series.push(new am4charts.ColumnSeries());
      this.columnSeries.strokeWidth = 0;
      this.columnSeries.yAxes = categoryAxis;
      this.columnSeries.dataFields.valueY = 'y';
      this.columnSeries.dataFields.categoryX = 'x';
      this.columnSeries.cursorTooltipEnabled = false;
      this.columnSeries.columns.template.height = am4core.percent(50);
      this.columnSeries.columns.template.width = 3;
      this.columnSeries.columns.template.fill = am4core.color(defaultFillColor);
      this.columnSeries.columns.template.propertyFields.fill = 'fillColor';
      this.columnSeries.tooltip.getFillFromObject = false;
      this.columnSeries.tooltip.background.fill = '#ffffff';

      // create circle bullet
      this.bulletSeries = this.columnSeries.bullets.push(new this.am4charts.Bullet());

      const circle = this.bulletSeries.createChild(this.am4core.Circle);
      circle.horizontalCenter = 'middle';
      circle.verticalCenter = 'middle';
      circle.strokeWidth = 0;
      circle.fill = defaultFillColor;
      circle.direction = 'top';
      circle.width = 14;
      circle.height = 14;

      // create label for bullet
      const labelBullet = this.columnSeries.bullets.push(new this.am4charts.LabelBullet());
      labelBullet.label.fill = this.am4core.color(fontColor);
      labelBullet.label.fontSize = 12;
      labelBullet.label.fontFamily = fontFamily;
    }

    if (!isEqual(this.prevChartData, data)) {
      chart.data = data;
      this.prevChartData = data;

      // Updates the title and tooltip when selection changes in PlacementAnalytics.
      this.valueAxis.title.text = yAxisTitle;
      this.columnSeries.columns.template.tooltipHTML = ReactDOMServer.renderToString(this.drawToolTip());
      this.bulletSeries.tooltipHTML = ReactDOMServer.renderToString(this.drawToolTip());
      if(isAvgCost) {
        this.valueAxis.numberFormatter.numberFormat = ".##";
      } else if (isAdVisit) {
        this.valueAxis.numberFormatter.numberFormat = ".#";
      }
    }

    return chart;
  }

  drawToolTip() {
    const { yAxisTitle } = this.props;
    return (
      <div className={styles.toolTip}>
        <div className={styles.title}>{yAxisTitle}</div>
        <div className={styles.divider}/>
        <div className={styles.data}>
          <span className={styles.dataTitle}>{'{categoryX}'}</span>
          <span className={styles.value}>{`{valueY}`}</span>
        </div>
      </div>
    );
  }

  setTitleFont(axis, text, color, weight = 500, size = 14) {
    axis.title.text = text;
    axis.title.fill = color;
    axis.title.fontSize = size;
    axis.title.fontWeight = weight;
  }

  setFont(series, size, color, weight = 400, fontFamily) {
    series.fontSize = size;
    series.fontWeight = weight;
    series.fontFamily = fontFamily;
    series.renderer.labels.template.fill = color;
  }
}
